import { NextRequest, NextResponse } from 'next/server'
import { eq, and } from 'drizzle-orm'
import { createTransport } from 'nodemailer'
import { auth } from '@/auth'
import { db } from '@/db'
import { tickets, ticketMessages, users, auditLog } from '@/db/schema'
import { inngest } from '@/lib/inngest'
import { transition } from '@/lib/ticket-state-machine'
import { buildFollowupReceivedEmail } from '@/lib/followup-received-email'

function sanitize(text: string): string {
  return text.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
}

export async function POST(
  request: NextRequest,
  { params }: { params: Promise<{ ticketId: string }> },
) {
  const session = await auth()
  if (!session?.user?.id) {
    return NextResponse.json({ error: 'Unauthorized' }, { status: 401 })
  }

  const { ticketId } = await params

  const body = await request.json().catch(() => null)
  if (!body?.submissionText || typeof body.submissionText !== 'string') {
    return NextResponse.json({ error: 'submissionText is required' }, { status: 400 })
  }

  const { submissionText, idempotencyKey } = body as {
    submissionText: string
    idempotencyKey?: string
  }

  const tenantId = session.user.tenantId
  if (!tenantId) {
    return NextResponse.json({ error: 'User has no tenant association' }, { status: 403 })
  }

  const userId = session.user.id

  const [ticket] = await db
    .select()
    .from(tickets)
    .where(and(eq(tickets.id, ticketId), eq(tickets.tenantId, tenantId)))
    .limit(1)

  if (!ticket) {
    return NextResponse.json({ error: 'Ticket not found' }, { status: 404 })
  }
  if (ticket.submitterId !== userId) {
    return NextResponse.json({ error: 'Forbidden' }, { status: 403 })
  }

  const retriageStatuses = ['NEEDS_INFO', 'AWAITING_REPLY'] as const
  const followUpStatuses = ['RESPONDED'] as const
  const isRetriage = (retriageStatuses as readonly string[]).includes(ticket.status)
  const isFollowUp = (followUpStatuses as readonly string[]).includes(ticket.status)

  if (!isRetriage && !isFollowUp) {
    return NextResponse.json(
      { error: `Cannot add message — ticket is in state ${ticket.status}` },
      { status: 409 },
    )
  }

  const [message] = await db
    .insert(ticketMessages)
    .values({
      tenantId,
      ticketId: ticket.id,
      authorId: userId,
      role: 'line_manager',
      bodyRaw: submissionText,
      bodySanitized: sanitize(submissionText),
      idempotencyKey: idempotencyKey ?? null,
    })
    .returning()

  if (isRetriage) {
    // Re-triage path: NEEDS_INFO or AWAITING_REPLY → SUBMITTED → AI pipeline
    transition(ticket.status as 'NEEDS_INFO' | 'AWAITING_REPLY', 'SUBMITTED')

    const [updated] = await db
      .update(tickets)
      .set({
        status: 'SUBMITTED',
        aiProcessingState: 'queued',
        lastMessageId: message.id,
        version: ticket.version + 1,
        updatedAt: new Date(),
      })
      .where(and(eq(tickets.id, ticketId), eq(tickets.version, ticket.version)))
      .returning()

    await db.insert(auditLog).values({
      tenantId,
      actorId: userId,
      actorType: 'user',
      action: 'ticket.status_changed',
      entityType: 'ticket',
      entityId: ticket.id,
      payload: { from: ticket.status, to: 'SUBMITTED', reason: 'line_manager_clarification' },
    })

    await inngest.send({
      name: 'ticket/submitted',
      data: {
        ticketId: ticket.id,
        tenantId,
        messageId: message.id,
        ticketVersion: updated.version,
      },
    })
  } else {
    // Follow-up path: RESPONDED → AWAITING_REPLY (no re-triage, notifies expert)
    transition('RESPONDED', 'AWAITING_REPLY')

    await db
      .update(tickets)
      .set({
        status: 'AWAITING_REPLY',
        lastMessageId: message.id,
        version: ticket.version + 1,
        updatedAt: new Date(),
      })
      .where(and(eq(tickets.id, ticketId), eq(tickets.version, ticket.version)))

    await db.insert(auditLog).values({
      tenantId,
      actorId: userId,
      actorType: 'user',
      action: 'ticket.status_changed',
      entityType: 'ticket',
      entityId: ticket.id,
      payload: { from: 'RESPONDED', to: 'AWAITING_REPLY', reason: 'line_manager_follow_up' },
    })
  }

  // Email confirmation to the submitter — fire-and-forget
  try {
    const [submitter] = await db
      .select({ email: users.email })
      .from(users)
      .where(eq(users.id, userId))
      .limit(1)

    if (submitter?.email && process.env.EMAIL_SERVER_HOST) {
      const transport = createTransport({
        host: process.env.EMAIL_SERVER_HOST,
        port: Number(process.env.EMAIL_SERVER_PORT ?? 587),
        auth: process.env.EMAIL_SERVER_USER
          ? { user: process.env.EMAIL_SERVER_USER, pass: process.env.EMAIL_SERVER_PASSWORD }
          : undefined,
      })
      const { subject, html, text } = buildFollowupReceivedEmail({
        ticketReference: ticket.referenceNumber ?? ticket.id,
        ticketTitle: ticket.title ?? 'Your ticket',
        ticketId: ticket.id,
      })
      await transport.sendMail({ to: submitter.email, from: process.env.EMAIL_FROM, subject, html, text })
    }
  } catch (err) {
    console.error('[messages] Failed to send follow-up confirmation email:', err)
  }

  return NextResponse.json({ ticketId: ticket.id, messageId: message.id }, { status: 201 })
}
